import { Room, RoomOptions, AudioCaptureOptions } from 'livekit-client'

export type LiveKitConfig = {
  url: string
  token: string
  roomName: string
  participantName: string
}

export type CollaborationSession = {
  userId: string
  roomName: string
  participantName: string
  simulationId: string
  organizationId: string
}

type EventHandler = (...args: unknown[]) => void

class LiveKitService {
  private room: Room | null = null
  private isConnected: boolean = false
  // Support multiple handlers per event type
  private eventHandlers: Map<string, Set<EventHandler>> = new Map()

  async connect(config: LiveKitConfig): Promise<Room> {
    if (this.room && this.isConnected) {
      throw new Error('Already connected to a room')
    }

    const roomOptions: RoomOptions = {
      adaptiveStream: true,
      dynacast: true,
      // Enhanced connectivity options to fix WebRTC issues
      reconnectPolicy: {
        nextRetryDelayInMs: (context) => {
          // Exponential backoff with max of 30s, stop after 10 retries (more resilient)
          if (context.retryCount >= 10) return -1 // -1 stops reconnection
          return Math.min(context.retryCount * 1500, 30000)
        },
      },
      // BUG FIX: Lower video quality to prevent bandwidth issues when multiple users enable cameras
      // This helps prevent other participants from getting disconnected
      videoCaptureDefaults: {
        resolution: {
          width: 480,  // Reduced from 640 to prevent bandwidth issues
          height: 360, // Reduced from 480
          frameRate: 20, // Reduced from 24 for lower bandwidth
        },
      },
      audioCaptureDefaults: {
        autoGainControl: true,
        echoCancellation: true,
        noiseSuppression: true,
      },
      // Publishing defaults - lower quality to prevent disconnection issues
      publishDefaults: {
        dtx: true, // Enable discontinuous transmission for audio to save bandwidth
        simulcast: true, // Enable simulcast for adaptive streaming (reduces bandwidth issues)
        stopMicTrackOnMute: false,
        videoCodec: 'vp8', // VP8 is more widely supported and uses less bandwidth than VP9/AV1
        // BUG FIX: Add video encoding settings to limit bandwidth
        videoEncoding: {
          maxBitrate: 500_000, // 500 kbps max to prevent bandwidth saturation
          maxFramerate: 20,
        },
        screenShareEncoding: {
          maxBitrate: 1_000_000, // 1 Mbps for screen share
          maxFramerate: 15,
        },
      },
      disconnectOnPageLeave: true,
      webAudioMix: false,
      // BUG FIX: Stop local tracks when unpublished to release resources properly
      stopLocalTrackOnUnpublish: true,
    }

    this.room = new Room(roomOptions)

    // Set up event listeners
    this.setupEventListeners()

    try {
      console.log('🚀 Attempting to connect to LiveKit server:', config.url)
      console.log('🔑 Using token for room:', config.roomName)

      await this.room.connect(config.url, config.token)
      this.isConnected = true
      console.log('✅ Successfully connected to LiveKit room:', config.roomName)

      // Enable data channels for reliable messaging
      await this.enableDataChannels()

      return this.room
    } catch (error) {
      console.error('❌ Failed to connect to LiveKit room:', error)
      this.room = null
      this.isConnected = false
      throw error
    }
  }

  private async enableDataChannels(): Promise<void> {
    if (!this.room) return

    try {
      // Ensure data channels are properly configured
      const localParticipant = this.room.localParticipant

      // Test data channel connectivity
      const testMessage = JSON.stringify({
        type: 'test',
        timestamp: new Date().toISOString(),
        message: 'Connection test'
      })

      const testData = new TextEncoder().encode(testMessage)
      await localParticipant.publishData(testData, { reliable: true })

      console.log('📡 DataChannel connectivity verified')
    } catch (error) {
      console.warn('⚠️ DataChannel test failed, but proceeding with connection:', error)
      // Don't throw here - data channels might work for actual messages
    }
  }

  async disconnect(): Promise<void> {
    if (this.room) {
      await this.room.disconnect()
      this.room = null
      this.isConnected = false
      console.log('Disconnected from LiveKit room')
    }
  }

  /**
   * Set the room reference from an external LiveKitRoom component.
   * This allows the livekitService singleton to share the same room connection
   * as the React LiveKitRoom component, enabling data channel communication
   * for features like vote syncing and choice proposals.
   */
  setRoom(room: Room | null): void {
    console.log('🔗 [LiveKit] setRoom called. Room exists:', !!room, 'Previous room:', !!this.room)
    this.room = room
    this.isConnected = room !== null

    if (room) {
      // Set up event listeners for the shared room
      this.setupEventListeners()
      console.log('✅ [LiveKit] Room reference set from LiveKitRoom component. Data channels now available.')
    } else {
      console.log('🔌 [LiveKit] Room reference cleared.')
    }
  }

  async enableCamera(enabled: boolean = true): Promise<void> {
    if (!this.room) throw new Error('Not connected to room')

    await this.room.localParticipant.setCameraEnabled(enabled)
  }

  async enableMicrophone(enabled: boolean = true): Promise<void> {
    if (!this.room) throw new Error('Not connected to room')

    if (enabled) {
      const audioOptions: AudioCaptureOptions = {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      }
      await this.room.localParticipant.setMicrophoneEnabled(true, audioOptions)
    } else {
      await this.room.localParticipant.setMicrophoneEnabled(false)
    }
  }

  async enableScreenShare(enabled: boolean = true): Promise<void> {
    if (!this.room) throw new Error('Not connected to room')
    await this.room.localParticipant.setScreenShareEnabled(enabled)
  }

  sendDataMessage(message: string, reliable: boolean = true): void {
    if (!this.room) throw new Error('Not connected to room')

    const data = new TextEncoder().encode(message)
    this.room.localParticipant.publishData(data, { reliable })
  }

  sendChatMessage(message: string, userName: string, userId: number): void {
    if (!this.room) throw new Error('Not connected to room')

    const chatMessage = {
      type: 'chat',
      id: `${Date.now()}-${Math.random()}`,
      message,
      userName,
      userId,
      timestamp: new Date().toISOString(),
      participantId: this.room.localParticipant.identity
    }

    const data = new TextEncoder().encode(JSON.stringify(chatMessage))
    this.room.localParticipant.publishData(data, { reliable: true })

    // Also emit locally so sender sees their own message
    this.emit('chatMessage', chatMessage)
  }

  sendTranscriptionSegment(segment: {
    text: string
    participantId: string
    timestamp: string
    confidence?: number
    isFinal: boolean
  }): void {
    if (!this.room) throw new Error('Not connected to room')

    const transcriptionData = {
      type: 'transcription',
      id: `${Date.now()}-${Math.random()}`,
      ...segment,
      timestamp: new Date().toISOString()
    }

    const data = new TextEncoder().encode(JSON.stringify(transcriptionData))
    this.room.localParticipant.publishData(data, { reliable: true })

    // Also emit locally
    this.emit('transcriptionSegment', transcriptionData)
  }

  // Collaboration-specific data channel methods

  sendParticipantJoined(participantData: Record<string, unknown>): void {
    if (!this.room) throw new Error('Not connected to room')

    const message = {
      type: 'participant_joined',
      ...participantData,
      timestamp: new Date().toISOString()
    }

    const data = new TextEncoder().encode(JSON.stringify(message))
    this.room.localParticipant.publishData(data, { reliable: true })
  }

  sendParticipantStatusChange(status: string): void {
    if (!this.room) throw new Error('Not connected to room')

    const message = {
      type: 'participant_status_changed',
      participantId: this.room.localParticipant.identity,
      status,
      timestamp: new Date().toISOString()
    }

    const data = new TextEncoder().encode(JSON.stringify(message))
    this.room.localParticipant.publishData(data, { reliable: true })
  }

  sendChoiceProposal(choice: Record<string, unknown>): void {
    if (!this.room) throw new Error('Not connected to room')

    const message = {
      type: 'choice_proposed',
      ...choice,
      timestamp: new Date().toISOString()
    }

    console.log('📤 [LiveKit] Broadcasting choice_proposed:', message)
    const data = new TextEncoder().encode(JSON.stringify(message))
    this.room.localParticipant.publishData(data, { reliable: true })
    console.log('📤 [LiveKit] choice_proposed broadcast complete')
  }

  sendVote(choiceId: string, decision: 'approve' | 'reject' | 'abstain', participantId?: string): void {
    if (!this.room) throw new Error('Not connected to room')

    const message = {
      type: 'vote_cast',
      choiceId,
      // Use the provided participantId (UUID from database) if given, otherwise fall back to LiveKit identity
      participantId: participantId || this.room.localParticipant.identity,
      decision,
      timestamp: new Date().toISOString()
    }

    console.log('📤 Broadcasting vote:', message)
    const data = new TextEncoder().encode(JSON.stringify(message))
    this.room.localParticipant.publishData(data, { reliable: true })
  }

  /**
   * Broadcast a resolved choice to all participants.
   * This is called when a vote tips the threshold and the choice is approved/rejected.
   * The full CollaborativeChoice is spread into the message so receivers get the complete data.
   */
  sendChoiceResolution(resolvedChoice: Record<string, unknown>): void {
    if (!this.room) throw new Error('Not connected to room')

    const message = {
      type: 'choice_resolved',
      ...resolvedChoice,
      timestamp: new Date().toISOString()
    }

    console.log('📤 [LiveKit] Broadcasting choice_resolved:', message)
    const data = new TextEncoder().encode(JSON.stringify(message))
    this.room.localParticipant.publishData(data, { reliable: true })
    console.log('📤 [LiveKit] choice_resolved broadcast complete')
  }

  getRoom(): Room | null {
    return this.room
  }

  isRoomConnected(): boolean {
    return this.isConnected && this.room !== null
  }

  // Event emitter methods for integration with collaboration system
  // Supports multiple handlers per event type
  on(event: string, handler: EventHandler): void {
    if (!this.eventHandlers.has(event)) {
      this.eventHandlers.set(event, new Set())
    }
    this.eventHandlers.get(event)!.add(handler)
    console.log(`🔧 [LiveKit] Handler registered for '${event}'. Total handlers for this event: ${this.eventHandlers.get(event)!.size}`)
  }

  // Remove a specific handler, or all handlers if no handler provided
  off(event: string, handler?: EventHandler): void {
    const handlers = this.eventHandlers.get(event)
    if (!handlers) {
      console.log(`🔧 [LiveKit] off('${event}'): No handlers exist for this event`)
      return
    }

    if (handler) {
      const deleted = handlers.delete(handler)
      console.log(`🔧 [LiveKit] off('${event}'): Handler ${deleted ? 'removed' : 'not found'}. Remaining handlers: ${handlers.size}`)
      // Clean up empty sets
      if (handlers.size === 0) {
        this.eventHandlers.delete(event)
      }
    } else {
      // Remove all handlers for this event
      this.eventHandlers.delete(event)
      console.log(`🔧 [LiveKit] off('${event}'): All handlers removed`)
    }
  }

  // Debug method to check handler registration
  getHandlerCount(event: string): number {
    return this.eventHandlers.get(event)?.size || 0
  }

  // Debug method to list all registered events
  listRegisteredEvents(): string[] {
    return Array.from(this.eventHandlers.keys())
  }

  private emit(event: string, ...args: unknown[]): void {
    const handlers = this.eventHandlers.get(event)
    const handlerCount = handlers ? handlers.size : 0

    console.log(`🎯 [LiveKit] === EMIT EVENT '${event}' ===`)
    console.log(`🎯 [LiveKit] Handlers registered: ${handlerCount}`)
    console.log(`🎯 [LiveKit] All registered events:`, this.listRegisteredEvents())

    if (handlers && handlers.size > 0) {
      let handlerIndex = 0
      handlers.forEach(handler => {
        try {
          console.log(`🎯 [LiveKit] Calling handler ${++handlerIndex}/${handlers.size} for '${event}'`)
          handler(...args)
          console.log(`🎯 [LiveKit] Handler ${handlerIndex} completed successfully`)
        } catch (error) {
          console.error(`❌ [LiveKit] Error in handler ${handlerIndex} for '${event}':`, error)
        }
      })
    } else {
      console.warn(`⚠️ [LiveKit] NO HANDLERS for event '${event}' - message will be lost!`)
      console.warn(`⚠️ [LiveKit] Available events:`, this.listRegisteredEvents())
    }
  }

  private setupEventListeners(): void {
    if (!this.room) return

    this.room.on('participantConnected', (participant) => {
      console.log('✅ Participant connected:', participant.identity)
      this.emit('participantConnected', participant)
    })

    this.room.on('participantDisconnected', (participant) => {
      console.log('👋 Participant disconnected:', participant.identity)
      this.emit('participantDisconnected', participant)
    })

    this.room.on('dataReceived', (payload, participant) => {
      try {
        const message = new TextDecoder().decode(payload)
        console.log('📨 [LiveKit] ========================================')
        console.log('📨 [LiveKit] DATA RECEIVED EVENT')
        console.log('�� [LiveKit] From:', participant?.identity || 'unknown')
        console.log('📨 [LiveKit] Raw message length:', message.length)
        console.log('📨 [LiveKit] Raw message (first 500 chars):', message.substring(0, 500))

        try {
          const data = JSON.parse(message)
          console.log('📨 [LiveKit] Parsed successfully!')
          console.log('📨 [LiveKit] Message type:', data.type)
          console.log('📨 [LiveKit] Message keys:', Object.keys(data))

          // Skip test messages to reduce log noise
          if (data.type === 'test') {
            console.log('📨 [LiveKit] Skipping test message')
            return
          }

          // Handle different types of data messages
          switch (data.type) {
            case 'chat':
              console.log('💬 [LiveKit] Processing CHAT message from', participant?.identity)
              this.emit('chatMessage', {
                ...data,
                participantId: participant?.identity
              })
              break

            case 'transcription':
              console.log('🎤 [LiveKit] Processing TRANSCRIPTION from', participant?.identity)
              this.emit('transcriptionSegment', {
                ...data,
                participantId: participant?.identity
              })
              break

            case 'participant_joined':
              console.log('👋 [LiveKit] Processing PARTICIPANT_JOINED:', participant?.identity)
              this.emit('participantJoined', {
                ...data,
                participantId: participant?.identity
              })
              break

            case 'participant_status_changed':
              console.log('🔄 [LiveKit] Processing PARTICIPANT_STATUS_CHANGED:', participant?.identity)
              this.emit('participantStatusChanged', {
                ...data,
                participantId: participant?.identity
              })
              break

            case 'choice_proposed':
              console.log('📝 [LiveKit] *** PROCESSING CHOICE_PROPOSED ***')
              console.log('📝 [LiveKit] From participant:', participant?.identity)
              console.log('📝 [LiveKit] Choice data:', JSON.stringify(data, null, 2))
              console.log('📝 [LiveKit] About to emit choiceProposed event...')
              this.emit('choiceProposed', {
                ...data,
                participantId: participant?.identity
              })
              console.log('📝 [LiveKit] choiceProposed event emitted!')
              break

            case 'vote_cast':
              console.log('🗳️ [LiveKit] *** PROCESSING VOTE_CAST ***')
              console.log('🗳️ [LiveKit] From participant (LiveKit):', participant?.identity)
              console.log('🗳️ [LiveKit] Vote data:', JSON.stringify(data, null, 2))
              console.log('🗳️ [LiveKit] About to emit voteCast event...')
              // Use participantId from the message (database UUID), not LiveKit identity
              this.emit('voteCast', {
                ...data,
                // Keep the original participantId from the message if present (database UUID)
                // Only fall back to LiveKit identity if not provided
                participantId: data.participantId || participant?.identity
              })
              console.log('🗳️ [LiveKit] voteCast event emitted!')
              break

            case 'choice_resolved':
              console.log('✅ [LiveKit] Processing CHOICE_RESOLVED:', data.choiceId)
              this.emit('choiceResolved', {
                ...data,
                participantId: participant?.identity
              })
              break

            default:
              console.log('❓ [LiveKit] Unknown message type:', data.type)
              // Fallback for legacy or unknown message types
              this.emit('dataReceived', { message, participantId: participant?.identity })
          }
        } catch (parseError) {
          console.warn('⚠️ [LiveKit] Failed to parse data message as JSON:', parseError)
          console.warn('⚠️ [LiveKit] Raw message was:', message)
          // If not JSON, treat as plain text message
          this.emit('dataReceived', { message, participantId: participant?.identity })
        }
      } catch (decodeError) {
        console.error('❌ [LiveKit] Failed to decode data message:', decodeError)
      }
    })

    // Enhanced connection state management
    this.room.on('disconnected', (reason) => {
      console.log('🔌 Disconnected from room. Reason:', reason)
      this.isConnected = false
      this.emit('disconnected', reason)
    })

    this.room.on('reconnecting', () => {
      console.log('🔄 Reconnecting to room...')
      this.emit('reconnecting')
    })

    this.room.on('reconnected', () => {
      console.log('✅ Reconnected to room successfully')
      this.isConnected = true
      this.emit('reconnected')
    })

    // Connection quality monitoring
    this.room.on('connectionQualityChanged', (quality, participant) => {
      if (participant) {
        console.log(`📊 Connection quality for ${participant.identity}:`, quality)
      } else {
        console.log('📊 Local connection quality:', quality)
      }
      this.emit('connectionQualityChanged', quality, participant)
    })

    // Enhanced error handling
    this.room.on('connectionStateChanged', (state) => {
      console.log('🔗 Connection state changed to:', state)
      this.emit('connectionStateChanged', state)
    })

    // WebRTC transport events for debugging
    this.room.on('signalConnected', () => {
      console.log('🔌 Signal connection established')
    })

    // Track changes for video/audio with better logging
    this.room.on('trackPublished', (publication, participant) => {
      console.log(`📹 Track published: ${publication.kind} from ${participant.identity}`)
      this.emit('trackPublished', publication, participant)
    })

    this.room.on('trackUnpublished', (publication, participant) => {
      console.log(`📹 Track unpublished: ${publication.kind} from ${participant.identity}`)
      this.emit('trackUnpublished', publication, participant)
    })

    this.room.on('trackSubscribed', (track, publication, participant) => {
      console.log(`📻 Track subscribed: ${track.kind} from ${participant.identity}`)
      this.emit('trackSubscribed', track, publication, participant)
    })

    this.room.on('trackUnsubscribed', (track, publication, participant) => {
      console.log(`📻 Track unsubscribed: ${track.kind} from ${participant.identity}`)
      this.emit('trackUnsubscribed', track, publication, participant)
    })

    // Additional error handling
    // Note: 'dataChannelError' is not a standard LiveKit Room event
    // DataChannel errors are handled through the general 'error' event
    this.room.on('connectionQualityChanged', (quality, participant) => {
      console.log(`📶 Connection quality changed for ${participant.identity}:`, quality)
    })
  }
}

// Export singleton instance
export const livekitService = new LiveKitService()

// API service for backend communication - integrates with existing collaboration API
export class LiveKitCollaborationAPI {
  private baseUrl: string

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl
  }

  async generateToken(session: CollaborationSession): Promise<LiveKitConfig> {
    // Get auth token - supports both team simulation tokens and regular user tokens
    const authToken = this.getAuthTokenSafe()
    const tokenSource = this.getTokenSource()

    console.log(`🔑 LiveKit token request - Auth token source: ${tokenSource}, Has token: ${!!authToken}`)

    // Build headers with auth token if available
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    }

    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`
    }

    let lastError: Error | null = null

    try {
      const response = await fetch(`${this.baseUrl}/saas/collaboration/livekit/token`, {
        method: 'POST',
        headers,
        credentials: 'include', // Include cookies for session-based auth
        body: JSON.stringify({
          userId: session.userId,
          roomName: session.roomName,
          participantName: session.participantName,
          permissions: {
            canPublish: true,
            canSubscribe: true,
            canPublishData: true,
            hidden: false,
          },
        }),
      })

      if (response.ok) {
        const data = await response.json() as { status: string; message?: string; data?: { livekitUrl: string; token: string; roomName: string; participantName: string } }

        if (data.status === 'success' && data.data) {
          console.log('🎉 Using real LiveKit token from backend!')
          return {
            url: data.data.livekitUrl,
            token: data.data.token,
            roomName: data.data.roomName,
            participantName: data.data.participantName,
          }
        }

        // API returned success but no data
        lastError = new Error(data.message || 'Backend returned success but no token data')
      } else {
        // Non-OK response - get error details
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`
        try {
          const errorData = await response.json()
          errorMessage = errorData.message || errorData.error || errorMessage
        } catch {
          // Response wasn't JSON, use status text
        }
        lastError = new Error(errorMessage)
        console.error('❌ LiveKit token API error:', errorMessage)
      }
    } catch (error) {
      // Network error, CORS error, or other fetch failure
      lastError = error instanceof Error ? error : new Error('Unknown network error')
      console.error('❌ LiveKit token request failed (possible CORS or network issue):', lastError.message)
    }

    // In production, don't silently fall back to demo mode - throw the error
    const isProduction = process.env.NODE_ENV === 'production' ||
                         window.location.hostname.includes('meseekna.com')

    if (isProduction) {
      throw new Error(
        `Failed to get LiveKit token: ${lastError?.message || 'Unknown error'}. ` +
        `Please check your connection and try again.`
      )
    }

    // Only use demo mode in development
    console.warn('⚠️ Using demo mode (development only) - backend token generation failed:', lastError?.message)
    return this.generateDemoToken(session)
  }

  private hasAuthToken(): boolean {
    return !!this.getAuthTokenSafe()
  }

  private getAuthToken(): string {
    const token = this.getAuthTokenSafe()
    if (!token) {
      throw new Error('No authentication token available')
    }
    return token
  }

  // Safe version that doesn't throw - returns null if no token
  private getAuthTokenSafe(): string | null {
    try {
      // First, check sessionStorage for team simulation token (per-tab isolation)
      const sessionToken = sessionStorage.getItem('token')
      if (sessionToken) {
        if (sessionToken.split('.').length === 3) {
          return sessionToken
        }
        console.warn('Found "token" in sessionStorage but it does not appear to be a valid JWT')
      }

      // Fall back to localStorage team simulation token (legacy)
      const teamSimToken = localStorage.getItem('token')
      if (teamSimToken) {
        // Validate it looks like a JWT (has 3 dot-separated parts)
        if (teamSimToken.split('.').length === 3) {
          return teamSimToken
        }
      }

      // Fall back to regular user token from persisted auth state
      // This is for logged-in users accessing team sims through the main app
      const auth = JSON.parse(localStorage.getItem('persist:msk:auth') || '{}')
      const user = JSON.parse(auth.user || '{}')
      if (user.token) {
        // Validate it looks like a JWT
        if (user.token.split('.').length === 3) {
          return user.token
        }
        console.warn('⚠️ Found token in persist:msk:auth but it does not appear to be a valid JWT')
      }

      return null
    } catch (e) {
      console.warn('⚠️ Error retrieving auth token:', e)
      return null
    }
  }

  // Helper to identify which token source is being used (for debugging)
  private getTokenSource(): string {
    try {
      const sessionToken = sessionStorage.getItem('token')
      if (sessionToken && sessionToken.split('.').length === 3) {
        return 'team-simulation-token (session)'
      }

      const teamSimToken = localStorage.getItem('token')
      if (teamSimToken && teamSimToken.split('.').length === 3) {
        return 'team-simulation-token (legacy)'
      }

      const auth = JSON.parse(localStorage.getItem('persist:msk:auth') || '{}')
      const user = JSON.parse(auth.user || '{}')
      if (user.token && user.token.split('.').length === 3) {
        return 'persisted-user-token'
      }

      return 'none'
    } catch {
      return 'error'
    }
  }

  private generateDemoToken(session: CollaborationSession): Promise<LiveKitConfig> {
    // Use real LiveKit URL from environment variables for demo mode
    const livekitUrl = process.env.REACT_APP_LIVEKIT_URL || 'wss://meseekna-team-sim-oxwnr2e5.livekit.cloud'

    console.log('🚀 Using demo LiveKit configuration with real server URL:', livekitUrl)

    return Promise.resolve({
      url: livekitUrl,
      token: 'demo-token-' + Date.now(), // Mock token - backend should generate real tokens
      roomName: session.roomName,
      participantName: session.participantName,
    })
  }
}

export const livekitCollaborationAPI = new LiveKitCollaborationAPI(
  process.env.REACT_APP_API_URL || "https://stageapi-simulations.meseekna.com"
)